import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BROWSER_LOCALE, Locale } from '../services/localization.service';
import {
  ConfigurationMap,
  ConfigurationPath,
  CustomPageMap,
  PathMap,
  PersonalizationMap,
  ReportsPath,
  RootPath,
  SysconfigPath,
} from '../utils/application-structure';
import { ReportFilterPreset } from '../utils/base-report-resource';
import { BASE_URL, ServerEvent } from '../utils/constants';
import { DeepPartial } from '../utils/types';

export type PersonalizationPage<T> = {
  config: T extends keyof PersonalizationMap ? PersonalizationMap[T] : undefined;
};

export type PersonalizationPages<T extends keyof any> = {
  [P in T]: PersonalizationPage<P>;
};

export interface PersonalizationContext<T extends RootPath> {
  pages: PersonalizationPages<PathMap[T]>;
}

export type PersonalizationContexts = {
  [P in RootPath]: PersonalizationContext<P>;
};

export type CustomPage<T> = {
  title: string;
  disabled: boolean;
  config: T extends keyof CustomPageMap ? CustomPageMap[T] : undefined;
};

export type CustomPages<T> = {
  [key: number]: CustomPage<T>
};

export type ConfigurationPage<T> = {
  disabled: boolean;
  config: T extends keyof ConfigurationMap ? ConfigurationMap[T] : undefined;
};

export type ConfigurationPages<T extends keyof any> = {
  [P in T]: ConfigurationPage<P>;
};

export interface ConfigurationContext<T extends RootPath> {
  disabled: boolean;
  pages: ConfigurationPages<PathMap[T]>;
  customPages: CustomPages<T>;
}

export type ConfigurationContexts = {
  [P in RootPath]: ConfigurationContext<P>;
};

export interface UiConfigConfiguration {
  pages: ConfigurationContexts;
  // Application wide configurations
  // contractors: string[],
  employees: {
    disabled: boolean,
  },
  items: {
    disabled: boolean,
  },
  vehicles: {
    disabled: boolean,
  },
}

export interface UiConfigPersonalization {
  pages: PersonalizationContexts;
  // Notifications
  notifications: ServerEvent[];
  // Series chart presets
  seriesChartFilterPresets?: ReportFilterPreset[];
  // Application wide personalization
  locale: Locale | typeof BROWSER_LOCALE;
  notificationsRefresh: number;
  toastHide: number;
  sortMapsByHeight: boolean;
  uiLinkTimeBeforeEvent: number;
  uiLinkTimeAfterEvent: number;
  signalDuration: number;
  signalButtonConfirm: boolean;
  serverTimeDelay: number;
  areaNonEmptyLabels: string[];
  hideEmptyAreas: boolean;
  gasHeatmapDuration: number;
  showGasHeatmapButton: boolean;
  emergencyListRefresh: number;
  showEmergencyListButton: boolean;
}

export interface UiConfig {
  configuration: UiConfigConfiguration;
  personalization: UiConfigPersonalization;
  version: string;
}

export const UI_CONFIG_VERSION = '1.0';

const DEFAULT_GLOBAL_CONFIG: DeepPartial<UiConfig> = {
  configuration: {
    pages: {
      configuration: {
        pages: {
          [ConfigurationPath.EQUIPMENT]: { disabled: true },
          [ConfigurationPath.TAGS]: { disabled: true },
          [ConfigurationPath.TAGS_CONFIG]: { disabled: true },
          [ConfigurationPath.ANCHORS]: { disabled: true },
          [ConfigurationPath.CUSTOM_NODES]: { disabled: true },
          [ConfigurationPath.BUILDINGS]: { disabled: true },
        },
      },
      reports: {
        pages: {
          [ReportsPath.ACTIVITY_ON_SITE]: { disabled: true },
          [ReportsPath.SITES_LOG_SUMMARY]: { disabled: true },
          [ReportsPath.DANGER_ZONES]: { disabled: true },
          [ReportsPath.SPEED_EXCEED]: { disabled: true },
          [ReportsPath.UNIDENTIFIED_ACCESS]: { disabled: true },
          // [ReportsPath.CONTRACTORS]: { disabled: true },
          [ReportsPath.MOTIONLESS_SUMMARY]: { disabled: true },
          // [ReportsPath.SERVICE_WORK]: { disabled: true },
          [ReportsPath.EXTERNAL_EVENTS]: { disabled: true },
          [ReportsPath.AREA_EVENTS]: { disabled: true },
          [ReportsPath.TAG_EVENTS]: { disabled: true },
          [ReportsPath.TAGZONE_EVENTS]: { disabled: true },
          [ReportsPath.TAG_COMMANDS]: { disabled: true },
          [ReportsPath.BUILDINGS_LOG]: { disabled: true },
          [ReportsPath.CAS_EVENTS]: { disabled: true },
          [ReportsPath.CAS_EVENTS_SUMMARY]: { disabled: true },
          [ReportsPath.CAS_EVENTS_SUMMARY_SERIES]: { disabled: true },
          [ReportsPath.ANCHOR_EVENTS]: { disabled: true },
        },
      },
      sysconfig: {
        pages: {
          [SysconfigPath.SIZ_TYPES]: { disabled: true },
          [SysconfigPath.GAS_SENSOR_SPECS]: { disabled: true },
          [SysconfigPath.AUDIT]: { disabled: true },
        },
      },
    },
  },
};

@Injectable({
  providedIn: 'root',
})
export class UiConfigsResource {
  constructor(private httpClient: HttpClient) {
  }

  getGlobalUiConfig(): Observable<DeepPartial<UiConfig>> {
    return this.httpClient.get<DeepPartial<UiConfig>>(`${BASE_URL}/uiconfig`).pipe(
      map((uiConfig) => (uiConfig.version !== UI_CONFIG_VERSION ? DEFAULT_GLOBAL_CONFIG : uiConfig)),
    );
  }

  saveGlobalUiConfig(uiConfig: DeepPartial<UiConfig>): Observable<void> {
    return this.httpClient.put<void>(`${BASE_URL}/uiconfig`, uiConfig);
  }

  getRoleUiConfig(role: number): Observable<DeepPartial<UiConfig>> {
    return this.httpClient.get<DeepPartial<UiConfig>>(`${BASE_URL}/roles/${role}/uiconfig`).pipe(
      map((uiConfig) => (uiConfig.version !== UI_CONFIG_VERSION ? {} : uiConfig)),
    );
  }

  saveRoleUiConfig(role: number, uiConfig: DeepPartial<UiConfig>): Observable<void> {
    return this.httpClient.put<void>(`${BASE_URL}/roles/${role}/uiconfig`, uiConfig);
  }

  getUserUiConfig(user: string): Observable<DeepPartial<UiConfig>> {
    return this.httpClient.get<DeepPartial<UiConfig>>(`${BASE_URL}/users/${user}/uiconfig`).pipe(
      map((uiConfig) => (uiConfig.version !== UI_CONFIG_VERSION ? {} : uiConfig)),
    );
  }

  saveUserUiConfig(user: string, uiConfig: DeepPartial<UiConfig>): Observable<void> {
    return this.httpClient.put<void>(`${BASE_URL}/users/${user}/uiconfig`, uiConfig);
  }
}
